/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { UNAVAILABLE_IMAGE_URL } from '../constants/general';
import { getOrderActionEligibility } from '../components/order-details/util/FulfillmentStatus';
import { setTrackingButtonVisibility } from '../components/order-details/util/TrackingLogics';
import { instoreOrderNumberFormat } from '../components/shared/order-number-formatter/OrderNumberFormatter';
import { FULFILLMENT_TYPE, STATUS_CODE } from '../constants/status';
import {
  ILineItem,
  IOPExpSessionData,
  IRouteParamBuilder,
  IShipment,
  TStateManagementFiltered,
  TStateManagementResponse,
  TTrialsObject,
  TTrialsResponse,
} from '../models/common';
import {
  IFulfillmentGroup,
  IOrderDetailsResponse,
} from '../models/order-details';
import { IOrderHistoryResponse } from '../models/purchase-history';
import { getCookieValue } from './cookie-helper';
import { convertToYYYYMMDD } from './date-utils';
import { isProductTypeEPPOnly } from './hdppCheck-utils';
import { getDisplayHeader } from './order-details/OrderDetailsUtils';
import { statusStepperData } from './order-history/card-utils';
import { getSvocId } from './user';

export const isSsr = typeof window === 'undefined';

// Checks if the current environment is not SSR.
export const getWindow = (): Window | undefined => {
  if (typeof window !== 'undefined') {
    return window;
  }
  return undefined;
};

export const getDocument = (): Document | undefined => {
  if (typeof document !== 'undefined') {
    return document;
  }
  return undefined;
};

export const getHost = (): string => {
  if (isSsr) return '';
  // Always use window.location.origin so port is included for local preview and dev
  return window.location.origin;
};

const isMobile =
  /Android|webOS|iPhone|iPad|iPod|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(
    getWindow()?.navigator?.userAgent ?? '',
  );

/**
 * Checks if the current environment is an LLC (Limited Liability Company) environment.
 * In this context, LLC refers to specific QA environments identified by their hostnames.
 * Returns true if the current hostname includes 'hd-qa71' or 'hd-qa74'.
 * Use this function to enable or disable features specific to these LLC environments.
 */
export const isLLC = (): boolean => {
  if (
    getWindow()?.location?.host?.includes('hd-qa71') ||
    getWindow()?.location?.host?.includes('hd-qa74')
  ) {
    return true;
  }
  return false;
};

export const getChannelID = (): number => (isMobile ? 2 : 1);

export const generateXB3TraceID = () =>
  Math.random().toString(16).substring(2) +
  new Date().getTime().toString(16).substring(0, 15);

/**
 * Gets number of pages for pagiantion components
 */
export const getNumPages = (
  pageLimit: number,
  recordsCount: number,
): number => {
  return Math.ceil(recordsCount / pageLimit) || 1;
};

/**
 * Format basic US phone number format.
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  // BODFS phone numbers are being returned from OVQ with hyphens, removing them
  const modPhonenumber = phoneNumber.replaceAll('-', '');

  if (modPhonenumber.length === 10) {
    return `${modPhonenumber.slice(0, 3)}-${modPhonenumber.slice(
      3,
      6,
    )}-${modPhonenumber.slice(6)}`;
  }

  if (modPhonenumber.length === 11) {
    return `${modPhonenumber.slice(0, 1)}-${modPhonenumber.slice(
      1,
      4,
    )}-${modPhonenumber.slice(4, 7)}-${modPhonenumber.slice(7)}`;
  }

  return modPhonenumber;
};

/**
 *
 */
export const floorNumberWithCommas = (
  num: number,
  decimalPostion: number,
): string => {
  if (num === 0) {
    return '0';
  }

  if (!num || Number.isNaN(num)) {
    return '';
  }

  return Math.floor(Number(num))
    .toFixed(decimalPostion)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export interface IFulfillments {
  [key: string]: [number, number];
}

/**
 *
 * @param customStartDate
 * @param customEndDate
 * @returns
 */

/**
 * Util used for DateSelectionDropdown.tsx
 */
export const dateSelectionCustomText = (
  customStartDate?: string,
  customEndDate?: string,
): string => {
  if (customStartDate && customEndDate) {
    const customStartDateTime = new Date(customStartDate);
    const start = convertToYYYYMMDD(customStartDateTime);
    const customEndDateTime = new Date(customEndDate);
    const end = convertToYYYYMMDD(customEndDateTime);
    return `${start} - ${end}`;
  }
  return 'Custom Date Range';
};

export const getTmxId = (): string => {
  let nativeTMXID: string | null = '';
  if (getWindow()?.__EXPERIENCE_CONTEXT__?.isConsumerApp) {
    nativeTMXID = getUrlParam('guid');
  }
  return (
    nativeTMXID || getWindow()?.THD_ACCOUNT_LIB?.TMX?.getTMXsessionId() || ''
  );
};

export const getUrlParam = (key: string) => {
  const searchParams =
    typeof window !== 'undefined' ? window?.location?.search : '';
  const urlParams = new URLSearchParams(searchParams);
  return urlParams.get(key);
};

/**
 * Functions used for userState, policy, trails and enrollment
 * set for left nav within server.js
 */
export const transformToTrialsObj = ({
  data,
}: {
  data: TTrialsResponse;
}): TTrialsObject => {
  const trials: TTrialsObject = {};
  data?.features?.forEach((feature) => {
    trials[feature] = true;
  });
  return trials;
};

export const filterOutPII = ({
  data,
}: {
  data: TStateManagementResponse;
}): TStateManagementFiltered => {
  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    userAddress,
    customer,
    ...needed
  } = data;

  const {
    phoneNumber: customerPhone,
    accountName,
    ...neededFromCustomer
  } = customer;
  return { ...needed, customer: { ...neededFromCustomer } };
};

const constructOrderNumberFn = (
  orderOrigin?: string,
  orderNumbers?: (string | undefined)[],
  orderNumber?: string,
  registerNumber?: number,
  transactionId?: number,
  receiptDetails?: string,
  localDelivery?: boolean,
) => {
  if (orderOrigin !== 'online' && orderOrigin !== 'Online') {
    // Checks if order is "H" order
    // Added a check for "W" orders, since online orders were appearing as in-store orders in the line items.
    if (
      ['H', 'W', 'C'].includes(orderNumbers?.[0]?.[0] ?? '') ||
      ['H', 'W', 'C'].includes(orderNumber?.[0] ?? '')
    ) {
      return orderNumbers?.[0] ?? orderNumber ?? '';
    }
    return instoreOrderNumberFormat(registerNumber, transactionId);
  }
  return orderNumbers?.[0] ?? orderNumber ?? receiptDetails ?? '';
};

export const routeBuilder = ({
  index,
  orderNumber,
  orderNumbers,
  orderOrigin,
  receiptDetails,
  registerNumber,
  salesDate,
  storeNumber,
  transactionId,
  transactionIdWithCheckDigit,
  transactionType,
  nickName,
  deliveryDate,
  workorderExceptions,
  POJobName,
  projectName,
  status,
  localDelivery,
}: IRouteParamBuilder) => {
  const formattedOrderOrigin = orderOrigin?.replace('#', '%23');
  const nonHOrder = orderNumbers?.find((order) => order?.[0] !== 'H');
  const constructOrderNumber = constructOrderNumberFn(
    orderOrigin,
    orderNumbers,
    orderNumber,
    registerNumber,
    transactionIdWithCheckDigit ?? transactionId,
    receiptDetails,
    localDelivery,
  );

  const transactionTypeModifier = (type: string | number | undefined) => {
    if (type === 'Return') return 'R';
    if (type === 'Sale') return 'S';
    return type;
  };

  return `?orderNumber=${
    (orderNumbers ?? []).length > 1 ? nonHOrder : constructOrderNumber
  }${salesDate ? '&salesDate=' + salesDate : ''}${
    storeNumber ? '&storeNumber=' + storeNumber : ''
  }${transactionId ? '&transactionId=' + transactionId : ''}${
    registerNumber ? '&registerNumber=' + registerNumber : ''
  }${
    transactionType
      ? '&transactionType=' + transactionTypeModifier(transactionType)
      : ''
  }${deliveryDate ? '&deliveryDate=' + deliveryDate : ''}${
    POJobName ? '&POJobName=' + encodeURIComponent(POJobName.trim()) : ''
  }${projectName ? '&projectName=' + projectName : ''}${
    status ? '&status=' + status : ''
  }${workorderExceptions ? '&workorderExceptions=' + workorderExceptions : ''}${
    formattedOrderOrigin
      ? '&orderOrigin=' + encodeURIComponent(formattedOrderOrigin.trim())
      : ''
  }${nickName ? '&nickName=' + nickName : ''}${
    index !== null && index !== undefined ? '&index=' + index : ''
  }${localDelivery ? '&localDelivery=' + localDelivery : ''}`;
};

export const checkForMultipleStatuses = (
  fulfillmentGroup: IFulfillmentGroup[],
  index: number,
  fulfillmentItem?: IFulfillmentGroup,
) => {
  const totalCount: IFulfillmentGroup[] = [];

  fulfillmentGroup.forEach((item) => {
    if (
      statusStepperData(null, 'title', item?.fulfillmentType) ===
      statusStepperData(
        null,
        'title',
        fulfillmentGroup?.[index]?.fulfillmentType,
      )
    ) {
      totalCount.push(item);
    }
  });
  if (totalCount.length > 1) {
    return `${
      totalCount.findIndex(
        (item) =>
          item?.lineItems?.length === fulfillmentItem?.lineItems?.length &&
          item?.lineItems?.[0]?.skuNumber ===
            fulfillmentItem?.lineItems?.[0]?.skuNumber,
      ) + 1
    } of ${totalCount.length}`;
  }
  return '';
};

export const checkHasGiftCard = (fulfillmentGroup: IFulfillmentGroup) => {
  return (
    fulfillmentGroup?.lineItems?.length ===
    fulfillmentGroup?.lineItems?.filter((item: ILineItem) => item?.isGiftCard)
      .length
  );
};

export const checkIsHDPPinAnyLineItem = (
  fulfillmentGroup: IFulfillmentGroup,
) => {
  /* 
    ProductTypeEPPOnly util function check if the lineItem has productType= 'EPP' or not which is HDPP lineItem.
    If any of the lineItem has HDPP order do not render DeliveryComponent at all to that LineItem
    isProductTypeEPPOnly()-->if return true --> has hdpp --> do not show delivery component
  */

  return isProductTypeEPPOnly(fulfillmentGroup?.lineItems || []);
};

export const checkEligibleForTracking = (
  fulfillmentGroup: IFulfillmentGroup,
  orderDetailsData: IOrderDetailsResponse,
) => {
  /* Corresponds with logic determining if Track Shipment is rendered in ActionButtons to determine if multiple shipments groups
  should be rendered for multiple shipments */

  const statusCode: STATUS_CODE =
    fulfillmentGroup?.lineItems?.[0]?.statusCode ?? 0;
  const fulfillmentType = fulfillmentGroup?.fulfillmentType as FULFILLMENT_TYPE;
  const { isTrackingEligibleForFulfillmentMethod } = getOrderActionEligibility(
    fulfillmentType,
    statusCode,
  );

  const isTrackingVisible = setTrackingButtonVisibility(
    statusCode,
    fulfillmentType,
    fulfillmentGroup,
  );

  return (
    isTrackingVisible &&
    isTrackingEligibleForFulfillmentMethod &&
    ['Online', 'online'].includes(orderDetailsData?.orderOrigin ?? '')
  );
};

export const createMultipleStatusesAndShipments = (
  fulfillmentGroups: IFulfillmentGroup[],
) => {
  const fulfillments: IFulfillments = {};
  fulfillmentGroups.forEach((fulfillmentGroup) => {
    const header = getDisplayHeader(fulfillmentGroup) ?? '';
    if (!fulfillments[header as keyof typeof fulfillments]) {
      fulfillments[header] = [1, 1];
    } else {
      fulfillments[header][1] += 1;
    }
  });
  return fulfillments;
};

export const checkForMultipleStatusesAndShipments = (
  header: string | undefined,
  multipleStatusesAndShipments: IFulfillments,
) => {
  if (header === undefined) return '';
  if (
    multipleStatusesAndShipments[
      header as keyof typeof multipleStatusesAndShipments
    ][1] === 1
  ) {
    return header;
  }
  if (
    multipleStatusesAndShipments[
      header as keyof typeof multipleStatusesAndShipments
    ]?.[0] <=
    multipleStatusesAndShipments[
      header as keyof typeof multipleStatusesAndShipments
    ]?.[1]
  ) {
    const headerText = `${header} ${
      multipleStatusesAndShipments[
        header as keyof typeof multipleStatusesAndShipments
      ]?.[0]
    } of ${
      multipleStatusesAndShipments[
        header as keyof typeof multipleStatusesAndShipments
      ]?.[1]
    }`;
    // eslint-disable-next-line no-param-reassign
    multipleStatusesAndShipments[
      header as keyof typeof multipleStatusesAndShipments
    ][0] =
      multipleStatusesAndShipments[
        header as keyof typeof multipleStatusesAndShipments
      ]?.[0] + 1;
    return headerText;
  }
  return '';
};

export const generateImageUrl = (
  lineItem: ILineItem | IOrderHistoryResponse,
) => {
  if ((lineItem as ILineItem)?.imageUrl) {
    return (lineItem as ILineItem)?.imageUrl;
  }
  if ((lineItem as ILineItem)?.giftCard?.thdImageUrl) {
    return (lineItem as ILineItem)?.giftCard?.thdImageUrl;
  }
  return UNAVAILABLE_IMAGE_URL;
};

// set of protected feature switches not allowed to be overwritten by a cookie
const PROTECTED_FS_SET = new Set(['allowSTHUndeliveredReturns']);

/**
 * Helper funciton used to determine if FS is turned on, FS can be overwritten via cookie
 * @param featureSwitchName FS Name
 * @returns true if FS is set to true or 1
 */

// Used when instance variable is not available in non-hooks
export const getFeatureSwitchValueStatic = (featureSwitchName = '') => {
  if (!getWindow()) return 'false';
  const isProd = window.location.hostname === 'www.homedepot.com';
  const isFSProtected = isProd && PROTECTED_FS_SET.has(featureSwitchName);

  if (!isFSProtected) {
    // Check cookies first
    const cookieValue = getCookieValue(featureSwitchName);
    if (cookieValue !== undefined) {
      return cookieValue;
    }
  }

  const featureSwitchValue =
    window.__EXPERIENCE_PROPS__?.expConfiguration?.[featureSwitchName];
  return featureSwitchValue;
};

export const getCookieByName = (name: string): string | null => {
  const prefix = `${name}=`;
  let begin = document.cookie.indexOf(`; ${prefix}`);

  let value = null;
  // cookie key/value pair is not the 2nd or further on.
  if (begin === -1) {
    begin = document.cookie.indexOf(prefix);
    if (begin !== 0) {
      return value;
    }
    const end = document.cookie.indexOf(';', begin);
    value = decodeURI(document.cookie.substring(begin + prefix.length, end));
  }
  // the cookie key/value pair does exist.
  begin += 2;
  let end = document.cookie.indexOf(';', begin);

  // the end of the cookie does not exist?
  if (end === -1) {
    end = document.cookie.length;
  }
  value = decodeURI(document.cookie.substring(begin + prefix.length, end));

  return value;
};

/**
 * Get the value of a feature switch from experience
 */
export const getFeatureSwitchValue = (
  fsName: string,
  isCookieOverwriteable?: boolean,
): boolean | string | number | null => {
  let selectedFs = getWindow()?.__EXPERIENCE_PROPS__?.expConfiguration[fsName];

  if (isCookieOverwriteable) {
    selectedFs = getFeatureSwitchValueStatic(fsName);
  }

  // Return false if the feature switch is not found
  if (selectedFs === undefined || selectedFs === null) {
    return null;
  }
  // Checks if the feature switch is a boolean then returns the value as a boolean
  if (selectedFs.toLocaleLowerCase() === 'true') {
    return true;
  }
  if (selectedFs.toLocaleLowerCase() === 'false') {
    return false;
  }
  // Checks if the feature switch is a number then returns the value as a number
  if (!Number.isNaN(Number(selectedFs))) {
    return Number(selectedFs);
  }
  // Returns the value as a string
  return selectedFs;
};

export const redirectToDownloadsPage = (
  redirectedFrom: 'linkedAccountsRedirect' | 'orderHistoryRedirect',
) => {
  setTimeout(() => {
    getWindow()?.sessionStorage.setItem(redirectedFrom, 'true');
    getWindow()?.location.assign('/myaccount/downloads');
  }, 3000);
};

export const PHODPageData = () => {
  let OPExpSessionData: string | undefined | null | IOPExpSessionData =
    getWindow()?.sessionStorage.getItem('purchaseHistoryPage');
  const userId = getSvocId();
  const defaultSessionData = {
    lastPage: 1,
    ODLastVisited: undefined,
    id: userId,
  };
  if (OPExpSessionData !== undefined && OPExpSessionData !== null) {
    OPExpSessionData = JSON.parse(OPExpSessionData);
  } else {
    OPExpSessionData = defaultSessionData;
  }

  if ((OPExpSessionData as IOPExpSessionData)?.id !== userId) {
    OPExpSessionData = defaultSessionData;
    getWindow()?.sessionStorage.removeItem('purchaseHistoryPage');
  }
  if ((OPExpSessionData as IOPExpSessionData)?.ODLastVisited !== undefined) {
    const dateNow = new Date();

    let dateDiff =
      (dateNow.getTime() -
        (new Date(
          (OPExpSessionData as IOPExpSessionData)?.ODLastVisited ?? '',
        )?.getTime() ?? 0)) /
      1000;

    dateDiff /= 60;
    dateDiff = Math.abs(Math.round(dateDiff));

    if (dateDiff > 3) {
      // Reset variable if last visited date is more than 10 minutes ago
      OPExpSessionData = defaultSessionData;
      getWindow()?.sessionStorage.removeItem('purchaseHistoryPage');
    }
  }

  return OPExpSessionData;
};

export const retrieveTrackingNumber = (shipments: IShipment[]) => {
  const trackingNumber = shipments?.find(
    (type) => type?.trackingType === 'LastMile',
  )?.trackingNumber;

  if (!trackingNumber && shipments?.[0]?.trackingNumber) {
    return shipments?.[0]?.trackingNumber;
  }

  return trackingNumber;
};

export const normalize = (value: string | null | undefined) =>
  (value || '').trim().toLowerCase().replace(/\s+/g, ' '); // Normalize: trims, converts to lowercase, and replaces multiple spaces with a single space
